import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState, type JSX, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import clsx from 'clsx';
import {
  Calendar,
  CalendarCheck,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Download,
  FileText,
  History,
  Layers,
  MessagesSquare,
  MoreHorizontal,
  Pencil,
  Plus,
  UploadCloud,
  UserPlus,
  Users,
} from 'lucide-react';
import { apiRequest, ApiError } from '../lib/api';
import { useToast } from '../lib/toast';
import { useAuthStore } from '../lib/auth-store';
import { useConfirm } from '../components/ui/Confirm';
import { formatDate } from '../lib/format';
import { Button } from '../components/ui/Button';
import { Drawer } from '../components/ui/Drawer';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { TextField, TextAreaField, SelectField } from '../components/ui/Field';
import { GatedSection } from '../components/shared/GatedSection';
import { DetailHeader } from '../components/shared/DetailHeader';
import { KpiStrip, type KpiItem } from '../components/shared/KpiStrip';
import { RelationPanel } from '../components/shared/RelationPanel';
import { ActivityPanel } from '../components/shared/ActivityPanel';
import { AddRelationModal } from '../components/shared/AddRelationModal';
import { EntityPicker, type PickedEntity } from '../components/shared/EntityPicker';
import {
  BatchDrawer,
  type BatchDrawerMode,
} from '../components/academics/BatchDrawer';
import {
  BatchModeBadge,
  BatchStatusBadge,
  batchLabel,
} from '../components/academics/StatusBadge';
import {
  DAY_OF_WEEK_LABEL,
  DAY_OF_WEEK_OPTIONS,
  humanBytes,
  parseSchedule,
  startOfWeekMonday,
  type AttendanceDay,
  type BatchRecord,
  type BatchScheduleSlot,
  type CourseSummary,
  type CourseWithLessons,
  type LessonSummary,
  type MeetingReport,
  type ResourceRecord,
  type WeeklyReport,
} from '../components/academics/types';
import type { TeacherRecord, ManagerRecord } from '../components/people/types';

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

export const Route = createFileRoute('/_authed/academics/batches/$batchId')({
  component: BatchDetailPage,
});

// ---------------------------------------------------------------------------
// Wire shapes — additive over BatchRecord so the inline Edit drawer still gets
// flat courseId/teacherId/schedule columns it needs for buildInitialValues,
// while the enriched detail GET embeds course/teacher/nalim cards.
// ---------------------------------------------------------------------------

interface CourseSummaryEmbed {
  id: string;
  name: string;
  slug: string;
  program: string;
}

interface TeacherEmbed {
  id: string;
  name: string;
  email: string | null;
}

interface NalimEmbed {
  id: string;
  name: string;
  email: string | null;
}

interface BatchDetail extends BatchRecord {
  course: CourseSummaryEmbed;
  teacher: TeacherEmbed;
  nalim: NalimEmbed | null;
}

interface BatchSummary {
  enrolledActive: number;
  enrolledCapacity: number;
  attendanceThisMonthPercent: number | null;
  weeklySheetStatus: string | null;
  sessionsThisWeek: number;
  pendingWeeklyTasks: number;
  openMeetingFollowUps: number;
}

interface EnrollmentRow {
  enrollment: {
    studentId: string;
    startDate: string;
    endDate: string | null;
    status: 'active' | 'paused' | 'completed' | 'withdrawn';
  };
  student: {
    id: string;
    name: string;
    admissionNumber: string;
    programMode: string;
    status: string;
  };
}

type ClassSessionStatus = 'scheduled' | 'in_progress' | 'completed' | 'cancelled';

interface ClassSessionRow {
  id: string;
  batchId: string;
  scheduledAt: string;
  durationMinutes: number;
  status: ClassSessionStatus;
  location: string | null;
  lessonId: string | null;
  notes: string | null;
  /** Timetable exception kind (migration 0020). */
  kind?: 'regular' | 'extra' | 'makeup';
  substituteTeacherId?: string | null;
  cancelReason?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface WeeklyTaskRow {
  id: string;
  batchId: string;
  weekStartDate: string;
  lessonId: string | null;
  description: string;
  expectedCompletion: string;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

type Tab =
  | 'overview'
  | 'students'
  | 'sessions'
  | 'attendance'
  | 'weekly'
  | 'weeklyTasks'
  | 'resources'
  | 'meetings'
  | 'history';

const TABS: ReadonlyArray<{ value: Tab; label: string; icon: typeof Users }> = [
  { value: 'overview', label: 'Overview', icon: FileText },
  { value: 'students', label: 'Students', icon: Users },
  { value: 'sessions', label: 'Sessions', icon: CalendarDays },
  { value: 'attendance', label: 'Attendance', icon: CalendarCheck },
  { value: 'weekly', label: 'Weekly sheet', icon: ClipboardList },
  { value: 'weeklyTasks', label: 'Weekly tasks', icon: ClipboardList },
  { value: 'resources', label: 'Resources', icon: Layers },
  { value: 'meetings', label: 'Meetings', icon: MessagesSquare },
  { value: 'history', label: 'History', icon: History },
];

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function BatchDetailPage(): JSX.Element {
  const { batchId } = Route.useParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const role = useAuthStore((s) => s.user?.primaryRole ?? null);
  const canManage =
    role === 'super_admin' || role === 'academic_admin' || role === 'nalim';

  const [tab, setTab] = useState<Tab>('overview');
  const [editDrawer, setEditDrawer] = useState<BatchDrawerMode | null>(null);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);

  const batchQuery = useQuery({
    queryKey: ['batch', 'detail', batchId] as const,
    queryFn: ({ signal }) =>
      apiRequest<BatchDetail>(`/api/batches/${encodeURIComponent(batchId)}`, { signal }),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const summaryQuery = useQuery({
    queryKey: ['batch', 'summary', batchId] as const,
    enabled: !!batchQuery.data,
    queryFn: ({ signal }) =>
      apiRequest<BatchSummary>(
        `/api/batches/${encodeURIComponent(batchId)}/summary`,
        { signal },
      ),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  // Lookups for the edit drawer + reassign pickers. Gated on canManage so we
  // don't burn three API calls for read-only viewers.
  const coursesQuery = useQuery({
    queryKey: ['courses', 'list'] as const,
    enabled: canManage,
    queryFn: ({ signal }) =>
      apiRequest<{ items: CourseSummary[] }>('/api/courses', { signal }),
    staleTime: 60_000,
  });

  const teachersQuery = useQuery({
    queryKey: ['teachers', 'lookup'] as const,
    enabled: canManage,
    queryFn: ({ signal }) =>
      apiRequest<{ items: TeacherRecord[] }>('/api/teachers', {
        signal,
        query: { page: 1, pageSize: 100 },
      }),
    staleTime: 60_000,
  });

  const managersQuery = useQuery({
    queryKey: ['managers', 'lookup'] as const,
    enabled: canManage,
    queryFn: ({ signal }) =>
      apiRequest<{ items: ManagerRecord[] }>('/api/managers', {
        signal,
        query: { page: 1, pageSize: 100 },
      }),
    staleTime: 60_000,
  });

  const batch = batchQuery.data;
  const summary = summaryQuery.data;

  const invalidateBatch = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['batch', 'detail', batchId] });
    void queryClient.invalidateQueries({ queryKey: ['batch', 'summary', batchId] });
    void queryClient.invalidateQueries({ queryKey: ['batches'] });
  };

  const invalidateEnrollments = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['batch', 'enrollments', batchId] });
    void queryClient.invalidateQueries({ queryKey: ['batch', 'summary', batchId] });
  };

  const statusMutation = useMutation({
    mutationFn: (nextStatus: BatchRecord['status']) =>
      apiRequest<{ ok: true }>(`/api/batches/${encodeURIComponent(batchId)}`, {
        method: 'PATCH',
        body: { status: nextStatus },
      }),
    onSuccess: (_res, nextStatus) => {
      toast(`Batch ${nextStatus}.`, { variant: 'success' });
      invalidateBatch();
    },
    onError: (err: unknown) => {
      toast(err instanceof ApiError ? err.message : 'Could not update status.', {
        variant: 'error',
      });
    },
  });

  const handleChangeStatus = async (next: BatchRecord['status']): Promise<void> => {
    if (!batch) return;
    const ok = await confirm({
      title: `Mark this batch as ${batchLabel[next].toLowerCase()}?`,
      description: `It is ${batchLabel[batch.status].toLowerCase()} now. The change is recorded in the activity log.`,
      confirmLabel: `Mark as ${batchLabel[next].toLowerCase()}`,
      variant: next === 'archived' ? 'danger' : 'default',
    });
    if (!ok) return;
    setOverflowOpen(false);
    statusMutation.mutate(next);
  };

  const kpiItems = useMemo<KpiItem[]>(() => {
    if (!batch) return [];
    const enrolled = summary
      ? `${summary.enrolledActive} / ${summary.enrolledCapacity}`
      : `— / ${batch.capacity}`;
    const attendance =
      summary?.attendanceThisMonthPercent != null
        ? `${summary.attendanceThisMonthPercent}%`
        : '—';
    return [
      { label: 'Enrolled', value: enrolled, onClick: () => setTab('students') },
      {
        label: 'Attendance',
        value: attendance,
        sub: 'this month',
        onClick: () => setTab('attendance'),
      },
      {
        label: 'Weekly sheet',
        value: summary?.weeklySheetStatus ?? 'Not started',
        onClick: () => setTab('weekly'),
      },
      {
        label: 'Sessions this week',
        value: summary?.sessionsThisWeek ?? '—',
        onClick: () => setTab('sessions'),
      },
      {
        label: 'Pending tasks',
        value: summary?.pendingWeeklyTasks ?? '—',
        onClick: () => setTab('weeklyTasks'),
      },
      {
        label: 'Meeting follow-ups',
        value: summary?.openMeetingFollowUps ?? '—',
        onClick: () => setTab('meetings'),
      },
    ];
  }, [batch, summary]);

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl mx-auto w-full">
      <GatedSection
        error={batchQuery.error}
        title="Batch"
        onRetry={() => batchQuery.refetch()}
      >
        {batchQuery.isLoading || !batch ? (
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <div className="space-y-6">
            <DetailHeader
              title={batch.name}
              sublabel={
                <>
                  <span>
                    course:{' '}
                    <span className="font-medium text-[color:var(--color-text)]">
                      {batch.course.name}
                    </span>
                  </span>
                  <span aria-hidden="true">·</span>
                  <span className="inline-flex items-center gap-1">
                    mode: <BatchModeBadge mode={batch.mode} />
                  </span>
                  <span aria-hidden="true">·</span>
                  <span className="tabular-nums">
                    {formatDate(batch.startDate)} →{' '}
                    {batch.endDate ? formatDate(batch.endDate) : 'ongoing'}
                  </span>
                </>
              }
              statusChip={<BatchStatusBadge status={batch.status} />}
              backTo="/academics/batches"
              actions={
                canManage ? (
                  <>
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<Pencil size={14} aria-hidden="true" />}
                      onClick={() => setEditDrawer({ kind: 'edit', id: batch.id })}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      icon={<UserPlus size={14} aria-hidden="true" />}
                      onClick={() => setEnrollOpen(true)}
                    >
                      Enroll student
                    </Button>
                    <OverflowMenu
                      open={overflowOpen}
                      onOpenChange={setOverflowOpen}
                      status={batch.status}
                      pending={statusMutation.isPending}
                      onChange={(next) => {
                        void handleChangeStatus(next);
                      }}
                    />
                  </>
                ) : null
              }
            />

            <KpiStrip items={kpiItems} loading={summaryQuery.isLoading && !summary} />

            <div
              role="tablist"
              aria-label="Batch sections"
              className="flex flex-wrap gap-1 border-b border-[color:var(--color-border)]"
            >
              {TABS.map(({ value, label, icon: Icon }) => {
                const active = tab === value;
                return (
                  <button
                    key={value}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setTab(value)}
                    className={clsx(
                      'inline-flex items-center gap-1.5 rounded-t-md px-3 py-2 text-sm font-medium transition-colors',
                      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500',
                      active
                        ? 'border-b-2 border-gold-500 text-[color:var(--color-text)]'
                        : 'text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]',
                    )}
                  >
                    <Icon size={14} aria-hidden="true" />
                    {label}
                  </button>
                );
              })}
            </div>

            {tab === 'overview' ? (
              <OverviewTab batch={batch} canManage={canManage} onChanged={invalidateBatch} />
            ) : null}
            {tab === 'students' ? (
              <StudentsTab
                batchId={batch.id}
                canManage={canManage}
                onEnrollOpen={() => setEnrollOpen(true)}
              />
            ) : null}
            {tab === 'sessions' ? (
              <SessionsTab batchId={batch.id} canManage={canManage} courseId={batch.course.id} />
            ) : null}
            {tab === 'attendance' ? <AttendanceTab batchId={batch.id} /> : null}
            {tab === 'weekly' ? <WeeklySheetTab batchId={batch.id} /> : null}
            {tab === 'weeklyTasks' ? (
              <WeeklyTasksTab
                batchId={batch.id}
                canManage={canManage}
                courseId={batch.course.id}
              />
            ) : null}
            {tab === 'resources' ? <ResourcesTab batchId={batch.id} /> : null}
            {tab === 'meetings' ? <MeetingsTab batchId={batch.id} /> : null}
            {tab === 'history' ? (
              <ActivityPanel targetType="batch" targetId={batch.id} title="Activity" />
            ) : null}
          </div>
        )}
      </GatedSection>

      {canManage && batch ? (
        <BatchDrawer
          mode={editDrawer}
          onClose={() => setEditDrawer(null)}
          initial={batch}
          isLoadingInitial={false}
          loadError={null}
          courses={coursesQuery.data?.items ?? []}
          teachers={teachersQuery.data?.items ?? []}
          managers={managersQuery.data?.items ?? []}
          onSaved={() => {
            setEditDrawer(null);
            invalidateBatch();
            toast('Batch saved.', { variant: 'success' });
          }}
        />
      ) : null}

      {canManage && batch ? (
        <EnrollStudentModal
          open={enrollOpen}
          onClose={() => setEnrollOpen(false)}
          batchId={batch.id}
          batchName={batch.name}
          onSuccess={() => {
            invalidateEnrollments();
            invalidateBatch();
            toast('Student enrolled.', { variant: 'success' });
          }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header overflow menu
// ---------------------------------------------------------------------------

interface OverflowMenuProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  status: BatchRecord['status'];
  pending: boolean;
  onChange: (next: BatchRecord['status']) => void;
}

function OverflowMenu({
  open,
  onOpenChange,
  status,
  pending,
  onChange,
}: OverflowMenuProps): JSX.Element {
  // Allowed transitions match the documented status guard from the plan:
  // draft → active → completed → archived; any → archived.
  const options: ReadonlyArray<{ value: BatchRecord['status']; label: string }> = useMemo(() => {
    const all: Array<{ value: BatchRecord['status']; label: string }> = [];
    if (status === 'draft') all.push({ value: 'active', label: 'Activate' });
    if (status === 'active') all.push({ value: 'completed', label: 'Mark completed' });
    if (status !== 'archived') all.push({ value: 'archived', label: 'Archive' });
    return all;
  }, [status]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent): void => {
      const target = e.target as HTMLElement | null;
      if (!target?.closest('[data-overflow-menu]')) onOpenChange(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onOpenChange]);

  if (options.length === 0) return <></>;

  return (
    <div className="relative" data-overflow-menu>
      <Button
        variant="ghost"
        size="sm"
        icon={<MoreHorizontal size={14} aria-hidden="true" />}
        onClick={() => onOpenChange(!open)}
        aria-haspopup="menu"
        aria-expanded={open}
        loading={pending}
      >
        More
      </Button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-10 mt-1 min-w-[180px] rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] py-1 shadow-lg"
        >
          {options.map((opt) => (
            <button
              key={opt.value}
              role="menuitem"
              type="button"
              onClick={() => onChange(opt.value)}
              className="block w-full px-3 py-2 text-left text-sm text-[color:var(--color-text)] hover:bg-[color:var(--color-surface)] focus:outline-none focus-visible:bg-[color:var(--color-surface)]"
            >
              {opt.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overview tab — schedule grid + teacher/nalim cards
// ---------------------------------------------------------------------------

interface OverviewTabProps {
  batch: BatchDetail;
  canManage: boolean;
  onChanged: () => void;
}

function OverviewTab({ batch, canManage, onChanged }: OverviewTabProps): JSX.Element {
  const schedule = useMemo(() => parseSchedule(batch.schedule), [batch.schedule]);

  return (
    <section className="grid gap-4 lg:grid-cols-3">
      <div className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5 lg:col-span-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
          Weekly schedule
        </h3>
        <ScheduleGrid schedule={schedule} />
      </div>
      <div className="space-y-4">
        <TeacherCard
          batchId={batch.id}
          teacher={batch.teacher}
          canManage={canManage}
          onChanged={onChanged}
        />
        <NalimCard
          batchId={batch.id}
          nalim={batch.nalim}
          canManage={canManage}
          onChanged={onChanged}
        />
      </div>
    </section>
  );
}

function ScheduleGrid({ schedule }: { schedule: BatchScheduleSlot[] }): JSX.Element {
  if (schedule.length === 0) {
    return (
      <p className="mt-3 text-sm text-[color:var(--color-text-muted)]">
        No schedule set. Edit the batch to add weekly slots.
      </p>
    );
  }
  // Group by day for a readable layout.
  const byDay = new Map<string, BatchScheduleSlot[]>();
  for (const slot of schedule) {
    const arr = byDay.get(slot.day) ?? [];
    arr.push(slot);
    byDay.set(slot.day, arr);
  }
  const orderedDays = DAY_OF_WEEK_OPTIONS.map((d) => d.value).filter((d) => byDay.has(d));
  return (
    <ul className="mt-3 divide-y divide-[color:var(--color-border)] rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)]">
      {orderedDays.map((day) => {
        const slots = byDay.get(day) ?? [];
        return (
          <li key={day} className="flex items-center gap-3 px-3 py-2">
            <span className="w-12 shrink-0 text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
              {DAY_OF_WEEK_LABEL[day]}
            </span>
            <div className="flex flex-wrap gap-2">
              {slots.map((s, i) => (
                <span
                  key={`${day}-${i}`}
                  className="inline-flex items-center rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2 py-0.5 text-xs tabular-nums text-[color:var(--color-text)]"
                >
                  {s.start} – {s.end}
                </span>
              ))}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

interface TeacherCardProps {
  batchId: string;
  teacher: TeacherEmbed;
  canManage: boolean;
  onChanged: () => void;
}

function TeacherCard({ batchId, teacher, canManage, onChanged }: TeacherCardProps): JSX.Element {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<PickedEntity | null>(null);

  const mutation = useMutation({
    mutationFn: (teacherId: string) =>
      apiRequest<{ ok: true }>(`/api/batches/${encodeURIComponent(batchId)}`, {
        method: 'PATCH',
        body: { teacherId },
      }),
  });

  const handleSubmit = async (): Promise<{ ok: true }> => {
    if (!picked?.id) {
      throw new ApiError(400, 'invalid', 'Select a teacher to assign.');
    }
    return mutation.mutateAsync(picked.id);
  };

  return (
    <div className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-4">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
        Teacher
      </h3>
      <p className="mt-2 text-sm font-medium text-[color:var(--color-text)]">{teacher.name}</p>
      {teacher.email ? (
        <p className="text-xs text-[color:var(--color-text-muted)]">{teacher.email}</p>
      ) : null}
      {canManage ? (
        <div className="mt-3">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setPicked(null);
              setOpen(true);
            }}
          >
            Reassign
          </Button>
        </div>
      ) : null}

      {canManage ? (
        <AddRelationModal<{ ok: true }>
          open={open}
          onClose={() => setOpen(false)}
          title="Reassign teacher"
          description="Pick the teacher who will take over this batch."
          picker={
            <EntityPicker
              entity="teacher"
              {...(picked ? { value: picked } : {})}
              onChange={(v) => {
                if (Array.isArray(v)) setPicked(v[0] ?? null);
                else setPicked(v.id ? v : null);
              }}
            />
          }
          submitLabel="Reassign"
          canSubmit={Boolean(picked?.id) && picked?.id !== teacher.id}
          onSubmit={handleSubmit}
          onSuccess={() => {
            toast('Teacher reassigned.', { variant: 'success' });
            onChanged();
          }}
        />
      ) : null}
    </div>
  );
}

interface NalimCardProps {
  batchId: string;
  nalim: NalimEmbed | null;
  canManage: boolean;
  onChanged: () => void;
}

function NalimCard({ batchId, nalim, canManage, onChanged }: NalimCardProps): JSX.Element {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<PickedEntity | null>(null);

  const mutation = useMutation({
    mutationFn: (nalimId: string | null) =>
      apiRequest<{ ok: true }>(`/api/batches/${encodeURIComponent(batchId)}`, {
        method: 'PATCH',
        body: { nalimId },
      }),
  });

  const handleSubmit = async (): Promise<{ ok: true }> => {
    if (!picked?.id) {
      throw new ApiError(400, 'invalid', 'Select a nalim to assign.');
    }
    return mutation.mutateAsync(picked.id);
  };

  const handleClear = async (): Promise<void> => {
    try {
      await mutation.mutateAsync(null);
      toast('Nalim removed.', { variant: 'success' });
      onChanged();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not remove nalim.', {
        variant: 'error',
      });
    }
  };

  return (
    <div className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-4">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
        Nalim
      </h3>
      {nalim ? (
        <>
          <p className="mt-2 text-sm font-medium text-[color:var(--color-text)]">{nalim.name}</p>
          {nalim.email ? (
            <p className="text-xs text-[color:var(--color-text-muted)]">{nalim.email}</p>
          ) : null}
        </>
      ) : (
        <p className="mt-2 text-sm text-[color:var(--color-text-muted)]">No nalim assigned.</p>
      )}
      {canManage ? (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setPicked(null);
              setOpen(true);
            }}
          >
            {nalim ? 'Reassign nalim' : 'Assign nalim'}
          </Button>
          {nalim ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                void handleClear();
              }}
              loading={mutation.isPending}
            >
              Remove
            </Button>
          ) : null}
        </div>
      ) : null}

      {canManage ? (
        <AddRelationModal<{ ok: true }>
          open={open}
          onClose={() => setOpen(false)}
          title={nalim ? 'Reassign nalim' : 'Assign nalim'}
          description="Pick the nalim who will oversee this batch."
          picker={
            <EntityPicker
              entity="manager"
              {...(picked ? { value: picked } : {})}
              onChange={(v) => {
                if (Array.isArray(v)) setPicked(v[0] ?? null);
                else setPicked(v.id ? v : null);
              }}
            />
          }
          submitLabel={nalim ? 'Reassign' : 'Assign'}
          canSubmit={Boolean(picked?.id) && picked?.id !== nalim?.id}
          onSubmit={handleSubmit}
          onSuccess={() => {
            toast(nalim ? 'Nalim reassigned.' : 'Nalim assigned.', { variant: 'success' });
            onChanged();
          }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Students tab — the headline enrollment surface
// ---------------------------------------------------------------------------

interface StudentsTabProps {
  batchId: string;
  canManage: boolean;
  onEnrollOpen: () => void;
}

function StudentsTab({ batchId, canManage, onEnrollOpen }: StudentsTabProps): JSX.Element {
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const queryClient = useQueryClient();
  const [pendingId, setPendingId] = useState<string | null>(null);

  const enrollmentsQuery = useQuery({
    queryKey: ['batch', 'enrollments', batchId] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ items: EnrollmentRow[] }>(
        `/api/batches/${encodeURIComponent(batchId)}/enrollments`,
        { signal },
      ),
  });

  const mutate = async (
    studentId: string,
    nextStatus: 'paused' | 'withdrawn' | 'active' | 'completed',
    endDate?: string,
  ): Promise<void> => {
    setPendingId(studentId);
    try {
      await apiRequest<{ ok: true }>(
        `/api/batches/${encodeURIComponent(batchId)}/enrollments/${encodeURIComponent(studentId)}`,
        {
          method: 'PATCH',
          body: endDate ? { status: nextStatus, endDate } : { status: nextStatus },
        },
      );
      toast(`Enrollment ${nextStatus}.`, { variant: 'success' });
      void queryClient.invalidateQueries({ queryKey: ['batch', 'enrollments', batchId] });
      void queryClient.invalidateQueries({ queryKey: ['batch', 'summary', batchId] });
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not update enrollment.', {
        variant: 'error',
      });
    } finally {
      setPendingId(null);
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  const askAndMutate = async (
    studentId: string,
    studentName: string,
    nextStatus: 'paused' | 'withdrawn' | 'active' | 'completed',
  ): Promise<void> => {
    const labels: Record<typeof nextStatus, { title: string; confirmLabel: string; variant?: 'danger' }> = {
      paused: { title: `Pause ${studentName}'s enrollment?`, confirmLabel: 'Pause' },
      active: { title: `Resume ${studentName}'s enrollment?`, confirmLabel: 'Resume' },
      withdrawn: {
        title: `Withdraw ${studentName} from this batch?`,
        confirmLabel: 'Withdraw',
        variant: 'danger',
      },
      completed: { title: `Mark ${studentName} as completed?`, confirmLabel: 'Mark completed' },
    };
    const opts = labels[nextStatus];
    const ok = await confirm({
      title: opts.title,
      ...(opts.variant ? { variant: opts.variant } : {}),
      confirmLabel: opts.confirmLabel,
    });
    if (!ok) return;
    if (nextStatus === 'withdrawn' || nextStatus === 'completed') {
      await mutate(studentId, nextStatus, today);
    } else {
      await mutate(studentId, nextStatus);
    }
  };

  const items = enrollmentsQuery.data?.items ?? [];
  const errorState = enrollmentsQuery.isError
    ? {
        message:
          enrollmentsQuery.error instanceof ApiError
            ? enrollmentsQuery.error.message
            : 'Could not load enrollments.',
        onRetry: (): void => {
          void enrollmentsQuery.refetch();
        },
      }
    : null;

  return (
    <RelationPanel
      title="Enrolled students"
      count={enrollmentsQuery.data ? items.length : undefined}
      description="Active enrolments for this batch."
      action={
        canManage ? (
          <Button
            size="sm"
            icon={<UserPlus size={14} aria-hidden="true" />}
            onClick={onEnrollOpen}
          >
            Enroll student
          </Button>
        ) : null
      }
      loading={enrollmentsQuery.isLoading && !enrollmentsQuery.data}
      error={errorState}
      isEmpty={!enrollmentsQuery.isLoading && items.length === 0}
      empty={{
        copy: 'No students enrolled yet. Use Enroll student to add one.',
        cta: canManage ? (
          <Button
            size="sm"
            icon={<UserPlus size={14} aria-hidden="true" />}
            onClick={onEnrollOpen}
          >
            Enroll student
          </Button>
        ) : undefined,
      }}
    >
      <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)]">
        <table className="w-full text-sm">
          <thead className="bg-[color:var(--color-surface)] text-left text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
            <tr>
              <th className="px-4 py-2">Student</th>
              <th className="px-4 py-2">Admission #</th>
              <th className="px-4 py-2">Start</th>
              <th className="px-4 py-2">End</th>
              <th className="px-4 py-2">Status</th>
              {canManage ? <th className="px-4 py-2 text-right">Actions</th> : null}
            </tr>
          </thead>
          <tbody>
            {items.map((row) => {
              const e = row.enrollment;
              const s = row.student;
              return (
                <tr
                  key={s.id}
                  className="border-t border-[color:var(--color-border)] hover:bg-[color:var(--color-surface)]"
                >
                  <td className="px-4 py-2 align-middle font-medium text-[color:var(--color-text)]">
                    {s.name}
                  </td>
                  <td className="px-4 py-2 align-middle font-mono text-xs text-[color:var(--color-text-muted)]">
                    {s.admissionNumber}
                  </td>
                  <td className="px-4 py-2 align-middle text-xs text-[color:var(--color-text-muted)]">
                    {formatDate(e.startDate)}
                  </td>
                  <td className="px-4 py-2 align-middle text-xs text-[color:var(--color-text-muted)]">
                    {e.endDate ? formatDate(e.endDate) : '—'}
                  </td>
                  <td className="px-4 py-2 align-middle">
                    <EnrollmentStatusChip status={e.status} />
                  </td>
                  {canManage ? (
                    <td className="px-4 py-2 align-middle">
                      <EnrollmentRowMenu
                        status={e.status}
                        disabled={pendingId === s.id}
                        onPause={() => {
                          void askAndMutate(s.id, s.name, 'paused');
                        }}
                        onResume={() => {
                          void askAndMutate(s.id, s.name, 'active');
                        }}
                        onWithdraw={() => {
                          void askAndMutate(s.id, s.name, 'withdrawn');
                        }}
                        onComplete={() => {
                          void askAndMutate(s.id, s.name, 'completed');
                        }}
                      />
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </RelationPanel>
  );
}

function EnrollmentStatusChip({
  status,
}: {
  status: EnrollmentRow['enrollment']['status'];
}): JSX.Element {
  const tone =
    status === 'active'
      ? 'bg-[color:var(--color-success)]/15 text-[color:var(--color-success)]'
      : status === 'paused'
        ? 'bg-gold-50 text-gold-700 dark:bg-gold-700/20'
        : status === 'completed'
          ? 'bg-[color:var(--color-surface)] text-[color:var(--color-text)]'
          : 'bg-[color:var(--color-surface)] text-[color:var(--color-text-muted)]';
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize',
        tone,
      )}
    >
      {status}
    </span>
  );
}

interface EnrollmentRowMenuProps {
  status: EnrollmentRow['enrollment']['status'];
  disabled: boolean;
  onPause: () => void;
  onResume: () => void;
  onWithdraw: () => void;
  onComplete: () => void;
}

function EnrollmentRowMenu({
  status,
  disabled,
  onPause,
  onResume,
  onWithdraw,
  onComplete,
}: EnrollmentRowMenuProps): JSX.Element {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent): void => {
      const target = e.target as HTMLElement | null;
      if (!target?.closest('[data-enrollment-row-menu]')) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative flex justify-end" data-enrollment-row-menu>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Enrollment actions"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="rounded-md p-1.5 text-[color:var(--color-text-muted)] hover:bg-[color:var(--color-surface)] hover:text-[color:var(--color-text)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <MoreHorizontal size={14} aria-hidden="true" />
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-7 z-10 min-w-[160px] rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] py-1 shadow-lg"
        >
          {status === 'active' ? (
            <MenuItem
              onClick={() => {
                setOpen(false);
                onPause();
              }}
            >
              Pause
            </MenuItem>
          ) : null}
          {status === 'paused' ? (
            <MenuItem
              onClick={() => {
                setOpen(false);
                onResume();
              }}
            >
              Resume
            </MenuItem>
          ) : null}
          {(status === 'active' || status === 'paused') ? (
            <MenuItem
              onClick={() => {
                setOpen(false);
                onComplete();
              }}
            >
              Mark completed
            </MenuItem>
          ) : null}
          {status !== 'withdrawn' ? (
            <MenuItem
              tone="danger"
              onClick={() => {
                setOpen(false);
                onWithdraw();
              }}
            >
              Withdraw
            </MenuItem>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function MenuItem({
  children,
  onClick,
  tone = 'default',
}: {
  children: ReactNode;
  onClick: () => void;
  tone?: 'default' | 'danger';
}): JSX.Element {
  return (
    <button
      role="menuitem"
      type="button"
      onClick={onClick}
      className={clsx(
        'block w-full px-3 py-2 text-left text-sm focus:outline-none',
        tone === 'danger'
          ? 'text-[color:var(--color-danger)] hover:bg-[color:var(--color-danger)]/10'
          : 'text-[color:var(--color-text)] hover:bg-[color:var(--color-surface)] focus-visible:bg-[color:var(--color-surface)]',
      )}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Enroll student modal
// ---------------------------------------------------------------------------

interface EnrollStudentModalProps {
  open: boolean;
  onClose: () => void;
  batchId: string;
  batchName: string;
  onSuccess: () => void;
}

function EnrollStudentModal({
  open,
  onClose,
  batchId,
  batchName,
  onSuccess,
}: EnrollStudentModalProps): JSX.Element {
  const today = new Date().toISOString().slice(0, 10);
  const [picked, setPicked] = useState<PickedEntity | null>(null);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState('');

  // Reset when reopened.
  useEffect(() => {
    if (open) {
      setPicked(null);
      setStartDate(today);
      setEndDate('');
    }
  }, [open, today]);

  const submit = async (): Promise<{ ok: true }> => {
    if (!picked?.id) {
      throw new ApiError(400, 'invalid', 'Select a student to enroll.');
    }
    const body: Record<string, string> = {
      studentId: picked.id,
      startDate,
    };
    if (endDate.trim()) body.endDate = endDate;
    return apiRequest<{ ok: true }>(
      `/api/batches/${encodeURIComponent(batchId)}/enrollments`,
      { method: 'POST', body },
    );
  };

  return (
    <AddRelationModal<{ ok: true }>
      open={open}
      onClose={onClose}
      title={`Enroll student in ${batchName}`}
      description="Pick a student, then set their start date. End date is optional."
      picker={
        <EntityPicker
          entity="student"
          {...(picked ? { value: picked } : {})}
          onChange={(v) => {
            if (Array.isArray(v)) setPicked(v[0] ?? null);
            else setPicked(v.id ? v : null);
          }}
          filter={{ batchId, exclude: 'active' }}
        />
      }
      fields={
        <>
          <TextField
            label="Start date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <TextField
            label="End date"
            type="date"
            optional
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </>
      }
      submitLabel="Enroll"
      canSubmit={Boolean(picked?.id) && Boolean(startDate)}
      onSubmit={submit}
      onSuccess={onSuccess}
    />
  );
}

// ---------------------------------------------------------------------------
// Sessions tab
// ---------------------------------------------------------------------------

// Local form schema for class sessions. The server validates the body with
// @bbl/validation's classSessionCreateSchema / classSessionUpdateSchema; this
// one describes the form's own fields (datetime-local text, empty-string
// selects) before they are mapped onto that body.
const sessionFormSchema = z
  .object({
    scheduledAt: z.string().min(1, 'Scheduled at is required'),
    durationMinutes: z.number().int().min(5).max(480),
    location: z.string().max(200).optional(),
    lessonId: z.string().uuid().optional(),
    notes: z.string().max(2000).optional(),
    status: z
      .enum(['scheduled', 'in_progress', 'completed', 'cancelled'])
      .optional(),
    kind: z.enum(['regular', 'extra', 'makeup']),
    substituteTeacherId: z.string().optional(),
    cancelReason: z.string().max(500).optional(),
  })
  .strict();

type SessionFormValues = z.infer<typeof sessionFormSchema>;

const ACADEMY_OFFSET_MS = 330 * 60_000;

/** Stored ISO (any offset) → `YYYY-MM-DDTHH:mm` in academy time (Asia/Kolkata). */
const isoToAcademyInput = (iso: string): string => {
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? '' : new Date(ms + ACADEMY_OFFSET_MS).toISOString().slice(0, 16);
};

/**
 * `YYYY-MM-DDTHH:mm` typed in academy time → ISO with an explicit +05:30
 * offset. The API's datetime validator requires an offset, and timetable
 * matching (lib/calendar/timetable.ts) needs the class's real local time.
 */
const academyInputToIso = (local: string): string =>
  `${local.length === 16 ? `${local}:00` : local}+05:30`;

const SESSION_KIND_LABEL: Record<'regular' | 'extra' | 'makeup', string> = {
  regular: 'Regular class',
  extra: 'Extra class',
  makeup: 'Make-up class',
};

interface SessionsTabProps {
  batchId: string;
  canManage: boolean;
  courseId: string;
}

function SessionsTab({ batchId, canManage, courseId }: SessionsTabProps): JSX.Element {
  const [drawerMode, setDrawerMode] = useState<
    { kind: 'create' } | { kind: 'edit'; session: ClassSessionRow } | null
  >(null);

  const sessionsQuery = useQuery({
    queryKey: ['batch', 'detail', batchId, 'sessions'] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ items: ClassSessionRow[] }>(
        `/api/batches/${encodeURIComponent(batchId)}/sessions`,
        { signal },
      ),
  });

  const items = sessionsQuery.data?.items ?? [];
  const hasSubstitutes = items.some((s) => s.substituteTeacherId);
  const teachersQuery = useQuery({
    queryKey: ['teachers', 'lookup'] as const,
    enabled: hasSubstitutes,
    queryFn: ({ signal }) =>
      apiRequest<{ items: TeacherRecord[] }>('/api/teachers', {
        signal,
        query: { page: 1, pageSize: 100 },
      }),
    staleTime: 60_000,
  });
  const substituteName = (teacherId: string): string =>
    teachersQuery.data?.items.find((t) => t.id === teacherId)?.name ?? 'another teacher';
  const errorState = sessionsQuery.isError
    ? {
        message:
          sessionsQuery.error instanceof ApiError
            ? sessionsQuery.error.message
            : 'Could not load sessions.',
        onRetry: (): void => {
          void sessionsQuery.refetch();
        },
      }
    : null;

  return (
    <>
      <RelationPanel
        title="Class sessions"
        count={sessionsQuery.data ? items.length : undefined}
        description="Scheduled and completed sessions for this batch."
        action={
          canManage ? (
            <Button
              size="sm"
              icon={<Plus size={14} aria-hidden="true" />}
              onClick={() => setDrawerMode({ kind: 'create' })}
            >
              Add session
            </Button>
          ) : null
        }
        loading={sessionsQuery.isLoading && !sessionsQuery.data}
        error={errorState}
        isEmpty={!sessionsQuery.isLoading && items.length === 0}
        empty={{
          copy: 'No sessions scheduled yet.',
          cta: canManage ? (
            <Button
              size="sm"
              icon={<Plus size={14} aria-hidden="true" />}
              onClick={() => setDrawerMode({ kind: 'create' })}
            >
              Add session
            </Button>
          ) : undefined,
        }}
      >
        <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)]">
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--color-surface)] text-left text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
              <tr>
                <th className="px-4 py-2">When</th>
                <th className="px-4 py-2 text-right">Duration</th>
                <th className="px-4 py-2">Location</th>
                <th className="px-4 py-2">Status</th>
                {canManage ? <th className="px-4 py-2 text-right">Edit</th> : null}
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr
                  key={s.id}
                  className="border-t border-[color:var(--color-border)] hover:bg-[color:var(--color-surface)]"
                >
                  <td className="px-4 py-2 align-middle text-[color:var(--color-text)]">
                    {formatDate(s.scheduledAt, { withTime: true })}
                    {s.kind && s.kind !== 'regular' ? (
                      <span className="ml-2 inline-flex items-center rounded-full bg-gold-50 px-2 py-0.5 text-[11px] font-semibold text-gold-900 dark:bg-gold-700/25 dark:text-gold-100">
                        {SESSION_KIND_LABEL[s.kind]}
                      </span>
                    ) : null}
                    {s.substituteTeacherId ? (
                      <span className="block text-xs text-[color:var(--color-text-muted)]">
                        Substitute:{' '}
                        {substituteName(s.substituteTeacherId)}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-2 text-right align-middle tabular-nums text-[color:var(--color-text-muted)]">
                    {s.durationMinutes} min
                  </td>
                  <td className="px-4 py-2 align-middle text-[color:var(--color-text-muted)]">
                    {s.location ?? '—'}
                  </td>
                  <td className="px-4 py-2 align-middle">
                    <SessionStatusChip status={s.status} />
                    {s.status === 'cancelled' && s.cancelReason ? (
                      <span className="block text-xs text-[color:var(--color-text-muted)]">{s.cancelReason}</span>
                    ) : null}
                  </td>
                  {canManage ? (
                    <td className="px-4 py-2 align-middle">
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => setDrawerMode({ kind: 'edit', session: s })}
                          aria-label={`Edit the class on ${formatDate(s.scheduledAt, { withTime: true })}`}
                          className="rounded-md p-1.5 text-[color:var(--color-text-muted)] hover:bg-[color:var(--color-surface)] hover:text-[color:var(--color-text)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500"
                        >
                          <Pencil size={14} aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </RelationPanel>

      {canManage ? (
        <SessionDrawer
          mode={drawerMode}
          batchId={batchId}
          courseId={courseId}
          onClose={() => setDrawerMode(null)}
        />
      ) : null}
    </>
  );
}

function SessionStatusChip({ status }: { status: ClassSessionStatus }): JSX.Element {
  const tone =
    status === 'completed'
      ? 'bg-[color:var(--color-success)]/15 text-[color:var(--color-success)]'
      : status === 'cancelled'
        ? 'bg-[color:var(--color-danger)]/15 text-[color:var(--color-danger)]'
        : status === 'in_progress'
          ? 'bg-gold-50 text-gold-700 dark:bg-gold-700/20'
          : 'bg-[color:var(--color-surface)] text-[color:var(--color-text-muted)]';
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize',
        tone,
      )}
    >
      {status.replace(/_/g, ' ')}
    </span>
  );
}

interface SessionDrawerProps {
  mode: { kind: 'create' } | { kind: 'edit'; session: ClassSessionRow } | null;
  batchId: string;
  courseId: string;
  onClose: () => void;
}

function SessionDrawer({ mode, batchId, courseId, onClose }: SessionDrawerProps): JSX.Element {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isEdit = mode?.kind === 'edit';

  // Lazy-load lessons of the parent course for the optional lesson picker.
  const courseQuery = useQuery({
    queryKey: ['course', 'detail', courseId, 'for-session-picker'] as const,
    enabled: mode !== null,
    queryFn: ({ signal }) =>
      apiRequest<CourseWithLessons>(`/api/courses/${encodeURIComponent(courseId)}`, { signal }),
    staleTime: 60_000,
  });

  // Teachers for the substitute picker (same cache key as the batch header).
  const teachersQuery = useQuery({
    queryKey: ['teachers', 'lookup'] as const,
    enabled: mode !== null,
    queryFn: ({ signal }) =>
      apiRequest<{ items: TeacherRecord[] }>('/api/teachers', {
        signal,
        query: { page: 1, pageSize: 100 },
      }),
    staleTime: 60_000,
  });

  const defaults = useMemo<SessionFormValues>(() => {
    if (mode?.kind === 'edit') {
      const s = mode.session;
      const out: SessionFormValues = {
        scheduledAt: isoToAcademyInput(s.scheduledAt),
        durationMinutes: s.durationMinutes,
        status: s.status,
        kind: s.kind ?? 'regular',
      };
      if (s.location) out.location = s.location;
      if (s.lessonId) out.lessonId = s.lessonId;
      if (s.notes) out.notes = s.notes;
      if (s.substituteTeacherId) out.substituteTeacherId = s.substituteTeacherId;
      if (s.cancelReason) out.cancelReason = s.cancelReason;
      return out;
    }
    return {
      scheduledAt: isoToAcademyInput(new Date().toISOString()),
      durationMinutes: 45,
      kind: 'regular',
    };
  }, [mode]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<SessionFormValues>({
    resolver: zodResolver(sessionFormSchema),
    defaultValues: defaults,
  });

  useEffect(() => {
    if (mode) reset(defaults);
  }, [mode, defaults, reset]);

  const watchedStatus = watch('status');

  const submit = handleSubmit(async (values) => {
    try {
      const body: Record<string, unknown> = {
        scheduledAt: academyInputToIso(values.scheduledAt),
        durationMinutes: values.durationMinutes,
        kind: values.kind,
      };
      if (values.location) body.location = values.location;
      if (values.lessonId) body.lessonId = values.lessonId;
      if (values.notes) body.notes = values.notes;
      if (values.substituteTeacherId) body.substituteTeacherId = values.substituteTeacherId;
      else if (isEdit) body.substituteTeacherId = null;
      if (isEdit && values.status) body.status = values.status;
      if (isEdit) {
        body.cancelReason =
          values.status === 'cancelled' && values.cancelReason?.trim() ? values.cancelReason.trim() : null;
      }

      if (mode?.kind === 'edit') {
        await apiRequest<{ ok: true }>(
          `/api/batches/${encodeURIComponent(batchId)}/sessions/${encodeURIComponent(mode.session.id)}`,
          { method: 'PATCH', body },
        );
        toast('Session updated.', { variant: 'success' });
      } else {
        await apiRequest<{ id: string }>(
          `/api/batches/${encodeURIComponent(batchId)}/sessions`,
          { method: 'POST', body },
        );
        toast('Session added.', { variant: 'success' });
      }
      void queryClient.invalidateQueries({
        queryKey: ['batch', 'detail', batchId, 'sessions'],
      });
      void queryClient.invalidateQueries({ queryKey: ['batch', 'summary', batchId] });
      onClose();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not save session.', {
        variant: 'error',
      });
    }
  });

  const lessonOptions = useMemo(
    () => [
      { value: '', label: 'No lesson linked' },
      ...((courseQuery.data?.lessons ?? []) as LessonSummary[]).map((l) => ({
        value: l.id,
        label: l.title,
      })),
    ],
    [courseQuery.data],
  );

  const substituteOptions = useMemo(
    () => [
      { value: '', label: 'No substitute — batch teacher takes it' },
      ...(teachersQuery.data?.items ?? []).map((t) => ({ value: t.id, label: t.name })),
    ],
    [teachersQuery.data],
  );

  const statusOptions: ReadonlyArray<{ value: ClassSessionStatus; label: string }> = [
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'in_progress', label: 'In progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  return (
    <Drawer
      open={mode !== null}
      onClose={onClose}
      title={isEdit ? 'Edit session' : 'Add session'}
      size="md"
    >
      <form onSubmit={submit} noValidate className="space-y-4">
        <SelectField
          label="Type"
          hint="Regular classes come from the batch timetable. Add a session here only when a class differs from it — an extra or make-up class, a substitute, or a cancellation."
          options={(['regular', 'extra', 'makeup'] as const).map((k) => ({ value: k, label: SESSION_KIND_LABEL[k] }))}
          error={errors.kind?.message}
          {...register('kind')}
        />
        <TextField
          label="Scheduled at (academy time)"
          type="datetime-local"
          error={errors.scheduledAt?.message}
          {...register('scheduledAt')}
        />
        <TextField
          label="Duration (minutes)"
          type="number"
          min={5}
          max={480}
          error={errors.durationMinutes?.message}
          {...register('durationMinutes', { valueAsNumber: true })}
        />
        <TextField
          label="Location"
          optional
          error={errors.location?.message}
          {...register('location', {
            setValueAs: (v: unknown) => {
              const s = typeof v === 'string' ? v.trim() : '';
              return s === '' ? undefined : s;
            },
          })}
        />
        <SelectField
          label="Lesson"
          optional
          options={lessonOptions}
          error={errors.lessonId?.message}
          {...register('lessonId', {
            setValueAs: (v: unknown) => {
              const s = typeof v === 'string' ? v.trim() : '';
              return s === '' ? undefined : s;
            },
          })}
        />
        <SelectField
          label="Substitute teacher"
          optional
          options={substituteOptions}
          error={errors.substituteTeacherId?.message}
          {...register('substituteTeacherId')}
        />
        {isEdit ? (
          <SelectField
            label="Status"
            options={statusOptions.map((o) => ({ value: o.value, label: o.label }))}
            error={errors.status?.message}
            {...register('status')}
          />
        ) : null}
        {isEdit && watchedStatus === 'cancelled' ? (
          <TextField
            label="Why was it cancelled?"
            optional
            hint="No attendance reminder or teacher report is expected for a cancelled class."
            error={errors.cancelReason?.message}
            {...register('cancelReason')}
          />
        ) : null}
        <TextAreaField
          label="Notes"
          optional
          rows={3}
          error={errors.notes?.message}
          {...register('notes', {
            setValueAs: (v: unknown) => {
              const s = typeof v === 'string' ? v.trim() : '';
              return s === '' ? undefined : s;
            },
          })}
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={isSubmitting}>
            {isEdit ? 'Save changes' : 'Add session'}
          </Button>
        </div>
      </form>
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// Attendance tab
// ---------------------------------------------------------------------------

function AttendanceTab({ batchId }: { batchId: string }): JSX.Element {
  const navigate = useNavigate();

  const daysQuery = useQuery({
    queryKey: ['batch', 'detail', batchId, 'attendance-days'] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ items: AttendanceDay[] }>('/api/attendance/days', {
        signal,
        query: { batchId },
      }),
  });

  const items = daysQuery.data?.items ?? [];
  const errorState = daysQuery.isError
    ? {
        message:
          daysQuery.error instanceof ApiError
            ? daysQuery.error.message
            : 'Could not load attendance days.',
        onRetry: (): void => {
          void daysQuery.refetch();
        },
      }
    : null;

  const openToday = (): void => {
    void navigate({
      to: '/academics/attendance',
      search: { batchId, date: new Date().toISOString().slice(0, 10) } as never,
    });
  };

  return (
    <RelationPanel
      title="Attendance days"
      count={daysQuery.data ? items.length : undefined}
      description="Days for which attendance has been opened."
      action={
        <Button
          size="sm"
          variant="secondary"
          icon={<Calendar size={14} aria-hidden="true" />}
          onClick={openToday}
        >
          Open today&rsquo;s attendance
        </Button>
      }
      loading={daysQuery.isLoading && !daysQuery.data}
      error={errorState}
      isEmpty={!daysQuery.isLoading && items.length === 0}
      empty={{
        copy: 'No attendance days yet.',
        cta: (
          <Button
            size="sm"
            icon={<Calendar size={14} aria-hidden="true" />}
            onClick={openToday}
          >
            Open today&rsquo;s attendance
          </Button>
        ),
      }}
    >
      <ul className="divide-y divide-[color:var(--color-border)] rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)]">
        {items.map((day) => (
          <li key={day.id}>
            <Link
              to="/academics/attendance"
              search={{ batchId, date: day.date } as never}
              className="flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-[color:var(--color-surface)]"
            >
              <span className="text-sm font-medium text-[color:var(--color-text)]">
                {formatDate(day.date)}
              </span>
              <span
                className={clsx(
                  'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold',
                  day.finalizedAt
                    ? 'bg-[color:var(--color-success)]/15 text-[color:var(--color-success)]'
                    : 'bg-gold-50 text-gold-700 dark:bg-gold-700/20',
                )}
              >
                {day.finalizedAt ? 'Finalised' : 'Open'}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </RelationPanel>
  );
}

// ---------------------------------------------------------------------------
// Weekly sheet tab
// ---------------------------------------------------------------------------

function WeeklySheetTab({ batchId }: { batchId: string }): JSX.Element {
  const navigate = useNavigate();

  const reportsQuery = useQuery({
    queryKey: ['batch', 'detail', batchId, 'weekly-sheet'] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ items: WeeklyReport[] }>('/api/weekly-sheet', {
        signal,
        query: { batchId },
      }),
  });

  const items = reportsQuery.data?.items ?? [];
  const errorState = reportsQuery.isError
    ? {
        message:
          reportsQuery.error instanceof ApiError
            ? reportsQuery.error.message
            : 'Could not load weekly sheets.',
        onRetry: (): void => {
          void reportsQuery.refetch();
        },
      }
    : null;

  const openSheets = (): void => {
    void navigate({
      to: '/academics/weekly-sheet',
      search: { batchId } as never,
    });
  };

  return (
    <RelationPanel
      title="Weekly sheets"
      count={reportsQuery.data ? items.length : undefined}
      description="Teacher-submitted weekly reports for this batch."
      action={
        <Button
          size="sm"
          variant="secondary"
          icon={<ClipboardList size={14} aria-hidden="true" />}
          onClick={openSheets}
        >
          Open weekly sheets
        </Button>
      }
      loading={reportsQuery.isLoading && !reportsQuery.data}
      error={errorState}
      isEmpty={!reportsQuery.isLoading && items.length === 0}
      empty={{ copy: 'No weekly sheets yet.' }}
    >
      <ul className="divide-y divide-[color:var(--color-border)] rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)]">
        {items.map((r) => (
          <li
            key={r.id}
            className="flex items-center justify-between gap-3 px-4 py-2.5"
          >
            <span className="text-sm font-medium text-[color:var(--color-text)]">
              Week of {formatDate(r.weekStartDate)}
            </span>
            <span className="inline-flex items-center rounded-full bg-[color:var(--color-surface)] px-2 py-0.5 text-[11px] font-semibold capitalize text-[color:var(--color-text-muted)]">
              {r.status.replace(/_/g, ' ')}
            </span>
          </li>
        ))}
      </ul>
    </RelationPanel>
  );
}

// ---------------------------------------------------------------------------
// Weekly tasks tab
// ---------------------------------------------------------------------------

const weeklyTaskFormSchema = z
  .object({
    weekStartDate: z.string().min(1),
    lessonId: z.string().uuid(),
    description: z.string().min(2).max(1000),
    expectedCompletion: z.string().min(1),
  })
  .strict();

type WeeklyTaskFormValues = z.infer<typeof weeklyTaskFormSchema>;

interface WeeklyTasksTabProps {
  batchId: string;
  canManage: boolean;
  courseId: string;
}

function WeeklyTasksTab({ batchId, canManage, courseId }: WeeklyTasksTabProps): JSX.Element {
  const [weekStart, setWeekStart] = useState<string>(() => startOfWeekMonday());
  const [drawerMode, setDrawerMode] = useState<
    { kind: 'create' } | { kind: 'edit'; task: WeeklyTaskRow } | null
  >(null);

  const tasksQuery = useQuery({
    queryKey: ['batch', 'detail', batchId, 'weekly-tasks', weekStart] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ items: WeeklyTaskRow[] }>(
        `/api/batches/${encodeURIComponent(batchId)}/weekly-tasks`,
        { signal, query: { week: weekStart } },
      ),
  });

  const items = tasksQuery.data?.items ?? [];
  const errorState = tasksQuery.isError
    ? {
        message:
          tasksQuery.error instanceof ApiError
            ? tasksQuery.error.message
            : 'Could not load weekly tasks.',
        onRetry: (): void => {
          void tasksQuery.refetch();
        },
      }
    : null;

  const shiftWeek = (deltaDays: number): void => {
    const d = new Date(`${weekStart}T00:00:00`);
    d.setDate(d.getDate() + deltaDays);
    setWeekStart(d.toISOString().slice(0, 10));
  };

  return (
    <>
      <RelationPanel
        title="Weekly tasks"
        count={tasksQuery.data ? items.length : undefined}
        description={`Week of ${formatDate(weekStart)}`}
        action={
          <div className="flex items-center gap-2">
            <WeekNavigator weekStart={weekStart} onShift={shiftWeek} onResetToThisWeek={() => setWeekStart(startOfWeekMonday())} />
            {canManage ? (
              <Button
                size="sm"
                icon={<Plus size={14} aria-hidden="true" />}
                onClick={() => setDrawerMode({ kind: 'create' })}
              >
                Add task
              </Button>
            ) : null}
          </div>
        }
        loading={tasksQuery.isLoading && !tasksQuery.data}
        error={errorState}
        isEmpty={!tasksQuery.isLoading && items.length === 0}
        empty={{
          copy: 'No tasks for this week yet.',
          cta: canManage ? (
            <Button
              size="sm"
              icon={<Plus size={14} aria-hidden="true" />}
              onClick={() => setDrawerMode({ kind: 'create' })}
            >
              Add task
            </Button>
          ) : undefined,
        }}
      >
        <ul className="divide-y divide-[color:var(--color-border)] rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)]">
          {items.map((t) => (
            <li
              key={t.id}
              className="flex items-start gap-3 px-4 py-3"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-[color:var(--color-text)]">
                  {t.description}
                </p>
                <p className="mt-0.5 text-xs text-[color:var(--color-text-muted)]">
                  Due {formatDate(t.expectedCompletion)}
                  {t.completedAt ? ` · Completed ${formatDate(t.completedAt)}` : ''}
                </p>
              </div>
              {canManage ? (
                <button
                  type="button"
                  onClick={() => setDrawerMode({ kind: 'edit', task: t })}
                  aria-label="Edit task"
                  className="shrink-0 rounded-md p-1.5 text-[color:var(--color-text-muted)] hover:bg-[color:var(--color-surface)] hover:text-[color:var(--color-text)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500"
                >
                  <Pencil size={14} aria-hidden="true" />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      </RelationPanel>

      {canManage ? (
        <WeeklyTaskDrawer
          mode={drawerMode}
          batchId={batchId}
          courseId={courseId}
          weekStart={weekStart}
          onClose={() => setDrawerMode(null)}
        />
      ) : null}
    </>
  );
}

function WeekNavigator({
  weekStart,
  onShift,
  onResetToThisWeek,
}: {
  weekStart: string;
  onShift: (deltaDays: number) => void;
  onResetToThisWeek: () => void;
}): JSX.Element {
  const isCurrentWeek = weekStart === startOfWeekMonday();
  return (
    <div className="inline-flex items-center gap-1">
      <button
        type="button"
        aria-label="Previous week"
        onClick={() => onShift(-7)}
        className="rounded-md p-1.5 text-[color:var(--color-text-muted)] hover:bg-[color:var(--color-surface)] hover:text-[color:var(--color-text)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500"
      >
        <ChevronLeft size={14} aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={onResetToThisWeek}
        disabled={isCurrentWeek}
        className="rounded-md px-2 py-1 text-xs font-medium text-[color:var(--color-text-muted)] hover:bg-[color:var(--color-surface)] hover:text-[color:var(--color-text)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500 disabled:cursor-not-allowed disabled:opacity-50"
      >
        This week
      </button>
      <button
        type="button"
        aria-label="Next week"
        onClick={() => onShift(7)}
        className="rounded-md p-1.5 text-[color:var(--color-text-muted)] hover:bg-[color:var(--color-surface)] hover:text-[color:var(--color-text)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500"
      >
        <ChevronRight size={14} aria-hidden="true" />
      </button>
    </div>
  );
}

interface WeeklyTaskDrawerProps {
  mode: { kind: 'create' } | { kind: 'edit'; task: WeeklyTaskRow } | null;
  batchId: string;
  courseId: string;
  weekStart: string;
  onClose: () => void;
}

function WeeklyTaskDrawer({
  mode,
  batchId,
  courseId,
  weekStart,
  onClose,
}: WeeklyTaskDrawerProps): JSX.Element {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isEdit = mode?.kind === 'edit';

  const courseQuery = useQuery({
    queryKey: ['course', 'detail', courseId, 'for-task-picker'] as const,
    enabled: mode !== null,
    queryFn: ({ signal }) =>
      apiRequest<CourseWithLessons>(`/api/courses/${encodeURIComponent(courseId)}`, { signal }),
    staleTime: 60_000,
  });

  const defaults = useMemo<WeeklyTaskFormValues>(() => {
    if (mode?.kind === 'edit') {
      const t = mode.task;
      return {
        weekStartDate: t.weekStartDate,
        lessonId: t.lessonId ?? '',
        description: t.description,
        expectedCompletion: t.expectedCompletion,
      };
    }
    // Default due date = end of the chosen week (Sunday).
    const sunday = new Date(`${weekStart}T00:00:00`);
    sunday.setDate(sunday.getDate() + 6);
    return {
      weekStartDate: weekStart,
      lessonId: '',
      description: '',
      expectedCompletion: sunday.toISOString().slice(0, 10),
    };
  }, [mode, weekStart]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<WeeklyTaskFormValues>({
    resolver: zodResolver(weeklyTaskFormSchema),
    defaultValues: defaults,
  });

  useEffect(() => {
    if (mode) reset(defaults);
  }, [mode, defaults, reset]);

  const submit = handleSubmit(async (values) => {
    try {
      if (mode?.kind === 'edit') {
        await apiRequest<{ ok: true }>(
          `/api/batches/${encodeURIComponent(batchId)}/weekly-tasks/${encodeURIComponent(mode.task.id)}`,
          {
            method: 'PATCH',
            body: {
              description: values.description,
              expectedCompletion: values.expectedCompletion,
              lessonId: values.lessonId,
              weekStartDate: values.weekStartDate,
            },
          },
        );
        toast('Task updated.', { variant: 'success' });
      } else {
        await apiRequest<{ id: string }>(
          `/api/batches/${encodeURIComponent(batchId)}/weekly-tasks`,
          {
            method: 'POST',
            body: {
              batchId,
              weekStartDate: values.weekStartDate,
              lessonId: values.lessonId,
              description: values.description,
              expectedCompletion: values.expectedCompletion,
            },
          },
        );
        toast('Task added.', { variant: 'success' });
      }
      void queryClient.invalidateQueries({
        queryKey: ['batch', 'detail', batchId, 'weekly-tasks'],
      });
      void queryClient.invalidateQueries({ queryKey: ['batch', 'summary', batchId] });
      onClose();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not save task.', {
        variant: 'error',
      });
    }
  });

  const lessons = (courseQuery.data?.lessons ?? []) as LessonSummary[];
  const lessonOptions = useMemo(
    () => [
      { value: '', label: lessons.length ? 'Select a lesson' : 'No lessons in this course' },
      ...lessons.map((l) => ({ value: l.id, label: l.title })),
    ],
    [lessons],
  );

  return (
    <Drawer
      open={mode !== null}
      onClose={onClose}
      title={isEdit ? 'Edit weekly task' : 'Add weekly task'}
      size="md"
    >
      <form onSubmit={submit} noValidate className="space-y-4">
        <TextField
          label="Week starts on"
          type="date"
          error={errors.weekStartDate?.message}
          {...register('weekStartDate')}
        />
        <SelectField
          label="Lesson"
          options={lessonOptions}
          error={errors.lessonId?.message}
          {...register('lessonId')}
        />
        <TextAreaField
          label="Description"
          rows={3}
          error={errors.description?.message}
          {...register('description')}
        />
        <TextField
          label="Expected completion"
          type="date"
          error={errors.expectedCompletion?.message}
          {...register('expectedCompletion')}
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={isSubmitting}>
            {isEdit ? 'Save changes' : 'Add task'}
          </Button>
        </div>
      </form>
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// Resources tab
// ---------------------------------------------------------------------------

const API_ORIGIN =
  (import.meta.env.VITE_API_ORIGIN as string | undefined) ??
  'https://api.babulilmonline.com';

function ResourcesTab({ batchId }: { batchId: string }): JSX.Element {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [downloadingIds, setDownloadingIds] = useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );

  const resourcesQuery = useQuery({
    queryKey: ['batch', 'detail', batchId, 'resources'] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ items: ResourceRecord[] }>('/api/resources', {
        signal,
        query: { batchId },
      }),
  });

  const items = resourcesQuery.data?.items ?? [];
  const errorState = resourcesQuery.isError
    ? {
        message:
          resourcesQuery.error instanceof ApiError
            ? resourcesQuery.error.message
            : 'Could not load resources.',
        onRetry: (): void => {
          void resourcesQuery.refetch();
        },
      }
    : null;

  const handleDownload = async (id: string): Promise<void> => {
    if (downloadingIds.has(id)) return;
    setDownloadingIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    try {
      const res = await apiRequest<{ url: string; expiresInSeconds: number }>(
        `/api/resources/${encodeURIComponent(id)}/download-url`,
      );
      window.open(`${API_ORIGIN}${res.url}`, '_blank', 'noopener');
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not start download.', {
        variant: 'error',
      });
    } finally {
      setDownloadingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const goToResources = (): void => {
    void navigate({
      to: '/academics/resources',
      search: { batchId } as never,
    });
  };

  return (
    <RelationPanel
      title="Resources"
      count={resourcesQuery.data ? items.length : undefined}
      description="Files scoped to this batch."
      action={
        <Button
          size="sm"
          variant="secondary"
          icon={<UploadCloud size={14} aria-hidden="true" />}
          onClick={goToResources}
        >
          Upload resource
        </Button>
      }
      loading={resourcesQuery.isLoading && !resourcesQuery.data}
      error={errorState}
      isEmpty={!resourcesQuery.isLoading && items.length === 0}
      empty={{
        copy: 'No resources attached to this batch yet.',
        cta: (
          <Button
            size="sm"
            icon={<UploadCloud size={14} aria-hidden="true" />}
            onClick={goToResources}
          >
            Upload resource
          </Button>
        ),
      }}
    >
      <ul className="divide-y divide-[color:var(--color-border)] rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)]">
        {items.map((resource) => {
          const isDownloading = downloadingIds.has(resource.id);
          return (
            <li key={resource.id} className="flex items-center gap-3 px-4 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-[color:var(--color-text)]">
                  {resource.title}
                </p>
                <p className="truncate text-xs text-[color:var(--color-text-muted)]">
                  <span className="font-mono">{resource.mimeType}</span>
                  <span className="mx-1.5">·</span>
                  {humanBytes(resource.sizeBytes)}
                  <span className="mx-1.5">·</span>
                  Uploaded {formatDate(resource.createdAt)}
                </p>
              </div>
              <Button
                size="sm"
                variant="secondary"
                icon={<Download size={14} aria-hidden="true" />}
                onClick={() => {
                  void handleDownload(resource.id);
                }}
                loading={isDownloading}
              >
                Download
              </Button>
            </li>
          );
        })}
        {items.length === 0 && resourcesQuery.isLoading ? (
          <li className="p-4">
            <EmptyState title="Loading…" />
          </li>
        ) : null}
      </ul>
    </RelationPanel>
  );
}

// ---------------------------------------------------------------------------
// Meetings tab
// ---------------------------------------------------------------------------

function MeetingsTab({ batchId }: { batchId: string }): JSX.Element {
  const navigate = useNavigate();

  const meetingsQuery = useQuery({
    queryKey: ['batch', 'detail', batchId, 'meetings'] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ items: MeetingReport[] }>('/api/meetings', {
        signal,
        query: { batchId, page: 1, pageSize: 100 },
      }),
  });

  const items = meetingsQuery.data?.items ?? [];
  const errorState = meetingsQuery.isError
    ? {
        message:
          meetingsQuery.error instanceof ApiError
            ? meetingsQuery.error.message
            : 'Could not load meetings.',
        onRetry: (): void => {
          void meetingsQuery.refetch();
        },
      }
    : null;

  const openMeetings = (): void => {
    void navigate({ to: '/academics/meetings' });
  };

  return (
    <RelationPanel
      title="Meeting reports"
      count={meetingsQuery.data ? items.length : undefined}
      description="Reports linked to this batch."
      action={
        <Button
          size="sm"
          variant="secondary"
          icon={<MessagesSquare size={14} aria-hidden="true" />}
          onClick={openMeetings}
        >
          Log meeting
        </Button>
      }
      loading={meetingsQuery.isLoading && !meetingsQuery.data}
      error={errorState}
      isEmpty={!meetingsQuery.isLoading && items.length === 0}
      empty={{ copy: 'No meetings logged for this batch yet.' }}
    >
      <ul className="divide-y divide-[color:var(--color-border)] rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)]">
        {items.map((m) => (
          <li key={m.id} className="px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-medium text-[color:var(--color-text)]">
                {formatDate(m.meetingAt, { withTime: true })}
              </p>
              <p className="text-xs text-[color:var(--color-text-muted)]">
                {m.attendees.length} attendee{m.attendees.length === 1 ? '' : 's'}
              </p>
            </div>
            <p className="mt-1 line-clamp-2 text-xs text-[color:var(--color-text-muted)]">
              {m.summary}
            </p>
          </li>
        ))}
      </ul>
    </RelationPanel>
  );
}
